import type { CSSProperties } from 'react';
import { BRAND_MARKS } from './brandMarks.generated.js';
import avatarDefault from '../../../brand/exports/avatars/runner-avatar-default-transparent.svg?url';
import avatarFocused from '../../../brand/exports/avatars/runner-avatar-focused-transparent.svg?url';
import avatarSurprised from '../../../brand/exports/avatars/runner-avatar-surprised-transparent.svg?url';
import avatarStrained from '../../../brand/exports/avatars/runner-avatar-strained-transparent.svg?url';
import avatarMischievous from '../../../brand/exports/avatars/runner-avatar-mischievous-transparent.svg?url';
import avatarHappy from '../../../brand/exports/avatars/runner-avatar-happy-transparent.svg?url';
import stickerRun from '../../../brand/exports/stickers/runner-sticker-run.svg?url';
import stickerWarmUp from '../../../brand/exports/stickers/runner-sticker-warm-up.svg?url';
import stickerStumble from '../../../brand/exports/stickers/runner-sticker-stumble.svg?url';
import stickerCoolDown from '../../../brand/exports/stickers/runner-sticker-cool-down.svg?url';
import stickerShoe from '../../../brand/exports/stickers/runner-sticker-shoe-celebration.svg?url';
import stickerFinish from '../../../brand/exports/stickers/runner-sticker-finish.svg?url';
import stickerOverheat from '../assets/runner-sticker-overheat.svg?url';

/*
 * RUNNER brand marks and character art (brand/docs/BRAND_GUIDE.md). Marks are inline path art painted with
 * `currentColor`, so ink-on-cream and cream-on-ink follow the theme; never stretch, rotate or recolour them per letter.
 * Character art is the pack's own vector exports, loaded as images.
 */

type MarkProps = { title?: string; className?: string; style?: CSSProperties; height?: number };

function Mark({ mark, title, className, style, height }: MarkProps & { mark: keyof typeof BRAND_MARKS }) {
  const m = BRAND_MARKS[mark];
  const [, , w, h] = m.viewBox.split(' ').map(Number);
  return (
    <svg
      viewBox={m.viewBox}
      height={height}
      width={height ? (height * w) / h : undefined}
      className={className}
      style={style}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <g dangerouslySetInnerHTML={{ __html: m.body }} />
    </svg>
  );
}

/** Path wordmark. `small` is the open-counter companion for 96–159 px; below 96 px use the emblem. */
export function Wordmark({ small = false, ...p }: MarkProps & { small?: boolean }) {
  return <Mark mark={small ? 'wordmarkSmall' : 'wordmark'} {...p} />;
}

/** The three-stride motion emblem. Keep its gaps open; minimum 16 px. */
export function Emblem(p: MarkProps) {
  return <Mark mark="emblem" {...p} />;
}

export type AvatarExpression = 'neutral' | 'focused' | 'surprised' | 'strained' | 'mischievous' | 'happy';
const AVATARS: Record<AvatarExpression, string> = {
  neutral: avatarDefault,
  focused: avatarFocused,
  surprised: avatarSurprised,
  strained: avatarStrained,
  mischievous: avatarMischievous,
  happy: avatarHappy,
};

/** The mascot head on a transparent ground. Minimum 32 px (use the emblem at 16 px). */
export function Avatar({ expression = 'neutral', size = 48, title, className, style }: { expression?: AvatarExpression; size?: number; title?: string; className?: string; style?: CSSProperties }) {
  return <img src={AVATARS[expression]} width={size} height={size} alt={title ?? ''} className={className} style={style} decoding="async" />;
}

/** Full-body poses: the pack's three-quarter stickers (plus Overheat, rendered from the same rig). */
export type MascotPose = 'run' | 'warmup' | 'celebrate' | 'finish' | 'overheat' | 'cool' | 'stumble' | 'wave' | 'rest' | 'forge';
const POSES: Record<MascotPose, string> = {
  run: stickerRun,
  warmup: stickerWarmUp,
  wave: stickerWarmUp,
  rest: stickerWarmUp,
  stumble: stickerStumble,
  cool: stickerCoolDown,
  celebrate: stickerShoe,
  finish: stickerFinish,
  forge: stickerFinish,
  overheat: stickerOverheat,
};

export function Mascot({
  pose = 'run',
  size = 220,
  title = 'RUNNER, the mascot',
  style,
  className,
}: {
  pose?: MascotPose;
  size?: number;
  title?: string;
  style?: CSSProperties;
  className?: string;
}) {
  return <img src={POSES[pose]} width={size} height={size} alt={title} className={className} style={style} decoding="async" draggable={false} />;
}
